/* Cassaforte · i wallet personali, cifrati nel browser.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * PERCHE' E' UNA SCHERMATA A PARTE E NON UNA SCHEDA DENTRO «WALLET»
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Perché sono due tabelle che si comportano in modo opposto, e tenerle nello
 * stesso posto è il modo di confonderle:
 *
 *  - `wallet` (schermata Wallet) è operativa. I tradebot ci firmano sopra le
 *    transazioni, la chiave la cifra il SERVER con la sua SECURITY_KEY, e il
 *    browser non deve toccarla. Cifrarla qui la romperebbe in modo
 *    silenzioso e irreversibile: il perché, per esteso, sta in testa a
 *    `pagine/wallet/ModuloWallet.tsx`.
 *  - `personal_wallet` (questa) è una cassaforte. Nessun bot la legge, il
 *    server non può aprirla, e la cifratura nel browser è l'unica che esiste.
 *
 * L'indirizzo pubblico è cifrato come il resto: è il motivo per cui la lista
 * mostra il nome e la data e nient'altro. Non è una dimenticanza, è quanto si
 * può sapere di una riga senza la password di chi l'ha scritta.
 */

import { useMemo, useState } from 'react';
import { Avvertenza, Pulsante } from '../componenti';
import { IntestazionePagina } from '../componenti/Guscio';
import { ContenitoreDati, ScheletroLista } from '../componenti/Stati';
import { Avvisi, usaAvvisi } from '../componenti/Sovrapposizioni';
import { personalWallets } from '../api/risorse';
import { usaRichiesta } from '../api/usaRichiesta';
import { dataOra } from '../lib/formato';
import { AperturaWallet } from './cassaforte/AperturaWallet';
import { ModuloNuovoWallet } from './cassaforte/ModuloNuovoWallet';
import { CAMPI_ELENCO, soloAnagrafica, type VoceCassaforte } from './cassaforte/dati';
import './cassaforte/cassaforte.css';

export function Cassaforte() {
  const { avvisi, mostra } = usaAvvisi();
  const [apreAggiunta, setApreAggiunta] = useState(false);
  const [daAprire, setDaAprire] = useState<VoceCassaforte | null>(null);

  const elenco = usaRichiesta(() => personalWallets.elenco(CAMPI_ELENCO), []);

  const lista: VoceCassaforte[] = useMemo(
    () => (elenco.dati?.data ?? []).map(soloAnagrafica),
    [elenco.dati],
  );

  return (
    <div className="sb-cornice">
      <Avvisi avvisi={avvisi} />

      <IntestazionePagina
        titolo="Cassaforte"
        sotto={
          <span className="sb-nota">
            {lista.length === 0
              ? 'nessun wallet custodito'
              : `${lista.length} ${lista.length === 1 ? 'wallet custodito' : 'wallet custoditi'} · cifrati in questo browser`}
          </span>
        }
        azioni={
          <Pulsante variante="primario" onClick={() => setApreAggiunta(true)}>
            Aggiungi
          </Pulsante>
        }
      />

      <div style={{ padding: 'var(--sb-pad-pagina)' }} className="sb-colonna">
        <Avvertenza livello="nota">
          Wallet custoditi, non operativi: nessun tradebot li usa. Ogni valore è cifrato nel browser
          con una password che non viene salvata da nessuna parte e non passa dal server. Se la
          password si perde, quel wallet non si riapre: non esiste un recupero.
        </Avvertenza>

        <ContenitoreDati
          stato={elenco.stato}
          causaVuoto="La cassaforte è vuota: non c'è nessun wallet personale salvato."
          azioneVuoto={{ etichetta: 'Salva il primo', onClick: () => setApreAggiunta(true) }}
          messaggioErrore={elenco.errore?.message}
          onRiprova={elenco.ricarica}
          scheletro={<ScheletroLista righe={3} conIcona={false} />}
        >
          {lista.map((v) => (
            <button key={v.id} type="button" className="sb-cs-scelta" onClick={() => setDaAprire(v)}>
              <div className="sb-riga" style={{ gap: 'var(--sb-s-4)' }}>
                <span className="sb-cs-scelta__nome">{v.nome}</span>
                <span className="sb-nota">{v.creatoIl ? dataOra(v.creatoIl) : ''}</span>
              </div>
              <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
                chiuso · serve la password per aprirlo
              </div>
            </button>
          ))}
        </ContenitoreDati>
      </div>

      <ModuloNuovoWallet
        aperto={apreAggiunta}
        onChiudi={() => setApreAggiunta(false)}
        nomiEsistenti={lista.map((v) => v.nome)}
        onCreato={(nome) => {
          setApreAggiunta(false);
          mostra(`Wallet "${nome}" cifrato e salvato.`, 'riuscito');
          elenco.ricarica();
        }}
      />

      <AperturaWallet
        voce={daAprire}
        onChiudi={() => setDaAprire(null)}
        onEliminato={(nome) => {
          setDaAprire(null);
          mostra(`Wallet "${nome}" eliminato.`, 'riuscito');
          elenco.ricarica();
        }}
        onErrore={(messaggio) => mostra(messaggio, 'fallito')}
      />
    </div>
  );
}
